import {
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal,
  Signal,
} from '@angular/core';
import {
  outputToObservable,
  takeUntilDestroyed,
} from '@angular/core/rxjs-interop';
import { TranslocoService } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import {
  addDays,
  addHours,
  differenceInCalendarDays,
  differenceInHours,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  isSameMonth,
  isToday,
  parseISO,
  setHours,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import {
  BlockType,
  Booking,
  BookingStatus,
} from '../../../core/models/entities/booking';
import { Room } from '../../../core/models/entities/room';
import { fromStore } from '../../../core/store';
import { bookingsActions } from '../../../core/store/bookings';
import {
  BookingsDisplayMode,
  BookingsScope,
} from '../../../core/store/bookings/bookings.state';
import {
  selectAllBookings,
  selectFocusedBooking,
} from '../../../core/store/reducers';
import { ModalService } from '../../../core/services/modal.service';
import { BookingDetailsModalComponent } from '../booking-details-modal/booking-details-modal.component';
import { PrivateBookingMainFormComponent } from '../private-booking-main-form/private-booking-main-form.component';
import { BookableBy } from '../../../core/models/common/bookable-by';
import { BookingCalendarNavigationService } from '../../../core/services/booking-calendar-navigation.service';
import { CalendarRoomsService } from '../../../core/services/calendar-rooms.service';
import { RoomColor } from '../../../core/helpers/room-color.helper';
import { BookingLastOccupiedDayHelper } from '../../../core/helpers/booking-last-occupied-day.helper';
import { BookingTooltipHelper } from '../../../core/helpers/booking-tooltip.helper';

const DEFAULT_NEW_BOOKING_START_HOUR = 8;
const LAST_USED_ROOM_STORAGE_KEY = 'bookings.lastUsedRoomId';
const DAYS_PER_WEEK = 7;
const HOURS_PER_DAY = 24;
const CELL_CHROME_HEIGHT_PX = 40;
const BOOKINGS_TOP_OFFSET_PX = 38;
const BOOKING_HEIGHT_PX = 24;
const BOOKING_GAP_PX = 4;
const BOOKING_LINE_HEIGHT_PX = BOOKING_HEIGHT_PX + BOOKING_GAP_PX;
const MORE_LINK_HEIGHT_PX = 20;
const DEFAULT_VISIBLE_BOOKINGS_PER_DAY = 3;
const MAX_VISIBLE_BOOKINGS_PER_DAY = 12;
const DEFAULT_ROW_HEIGHT_PX =
  CELL_CHROME_HEIGHT_PX +
  DEFAULT_VISIBLE_BOOKINGS_PER_DAY * BOOKING_LINE_HEIGHT_PX +
  MORE_LINK_HEIGHT_PX;

interface MonthWeekBooking {
  booking: Booking;
  color: RoomColor;
  startTime: string;
  isAllDay: boolean;
  tooltip: string;
  firstDayIndex: number;
  lastDayIndex: number;
  continuesFromPreviousWeek: boolean;
  continuesToNextWeek: boolean;
  lane: number;
}

interface MonthDay {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
}

interface MonthWeek {
  key: string;
  days: MonthDay[];
  bookings: MonthWeekBooking[];
  laneCount: number;
  maxRowHeight: number;
}

interface RowResize {
  weekKey: string;
  startY: number;
  startHeight: number;
}

@Component({
  selector: 'app-bookings-month-view',
  template: `
    <div class="relative overflow-hidden rounded-xl border border-gray-300">
      <div
        class="grid grid-cols-7 border-b border-gray-300 bg-gray-50 text-center text-xs font-medium tracking-wide text-gray-500 uppercase"
      >
        @for (weekDay of weekDayLabels(); track $index) {
          <div class="py-2.5">{{ weekDay | dateFns: 'EEE' }}</div>
        }
      </div>

      @for (week of monthWeeks(); track week.key; let lastWeek = $last) {
        <div
          class="relative grid grid-cols-7 border-gray-300"
          [class.border-b]="!lastWeek"
          [style.height.px]="rowHeightOf(week)"
        >
          @for (
            day of week.days;
            track day.date.getTime();
            let lastDay = $last
          ) {
            <div
              class="flex flex-col overflow-hidden border-gray-300 p-1.5"
              [class.border-r]="!lastDay"
              [class.bg-gray-50]="!day.isCurrentMonth"
              (click)="openCreateBookingModal(day.date)"
            >
              <button
                type="button"
                class="inline-flex size-7 shrink-0 cursor-pointer items-center justify-center self-end rounded-full text-xs font-medium hover:bg-gray-100"
                [ngClass]="{
                  'bg-primary-500 hover:bg-primary-600 text-white': day.isToday,
                  'text-gray-800': day.isCurrentMonth && !day.isToday,
                  'text-gray-400': !day.isCurrentMonth,
                }"
                (click)="$event.stopPropagation(); openDay(day.date)"
              >
                {{ day.date | dateFns: 'd' }}
              </button>
            </div>
          }

          <div
            class="pointer-events-none absolute inset-x-0 grid grid-cols-7"
            [style.top.px]="BOOKINGS_TOP_OFFSET_PX"
            [style.grid-auto-rows.px]="BOOKING_HEIGHT_PX"
            [style.row-gap.px]="BOOKING_GAP_PX"
          >
            @for (
              weekBooking of visibleBookingsOf(week);
              track weekBooking.booking.id
            ) {
              <button
                type="button"
                class="pointer-events-auto mx-1.5 flex min-w-0 cursor-pointer items-center gap-1.5 rounded-md border-l-[3px] px-1.5 text-left text-xs transition-colors"
                [ngClass]="[weekBooking.color.pill, weekBooking.color.accent]"
                [class.rounded-l-none]="weekBooking.continuesFromPreviousWeek"
                [style.border-left-width.px]="
                  weekBooking.continuesFromPreviousWeek ? 0 : 3
                "
                [class.rounded-r-none]="weekBooking.continuesToNextWeek"
                [class.mr-0]="weekBooking.continuesToNextWeek"
                [class.ml-0]="weekBooking.continuesFromPreviousWeek"
                [class.opacity-60]="isPending(weekBooking.booking)"
                [style.grid-column]="gridColumnOf(weekBooking)"
                [style.grid-row]="weekBooking.lane + 1"
                [tooltip]="weekBooking.tooltip"
                [followCursorHorizontally]="true"
                [style.cursor]="'pointer'"
                (click)="
                  $event.stopPropagation(); openBooking(weekBooking.booking)
                "
              >
                <app-booking-characteristic-icons
                  class="text-[10px]"
                  [booking]="weekBooking.booking"
                />
                @if (weekBooking.isAllDay) {
                  <i class="far fa-calendar-day shrink-0"></i>
                } @else if (!weekBooking.continuesFromPreviousWeek) {
                  <span class="shrink-0 font-semibold tabular-nums">{{
                    weekBooking.startTime
                  }}</span>
                }
                <span class="truncate">{{
                  weekBooking.booking.company ?? weekBooking.booking.name
                }}</span>
                @if (weekBooking.booking.status === BookingStatus.Pending) {
                  <span
                    class="ml-auto size-2 shrink-0 rounded-full bg-pink-600"
                  ></span>
                }
              </button>
            }

            @for (
              day of week.days;
              track day.date.getTime();
              let dayIndex = $index
            ) {
              @if (hiddenCountOn(week, dayIndex); as hiddenCount) {
                <button
                  type="button"
                  class="pointer-events-auto mx-1.5 cursor-pointer px-1.5 text-left text-xs font-medium text-gray-700 hover:text-gray-900"
                  [style.grid-column]="dayIndex + 1"
                  [style.grid-row]="visibleLaneCountOf(week) + 1"
                  (click)="$event.stopPropagation(); openDay(day.date)"
                >
                  {{
                    'bookings.month-view.more'
                      | transloco: { count: hiddenCount }
                  }}
                </button>
              }
            }
          </div>

          @if (canExpand(week)) {
            <div
              class="group absolute inset-x-0 -bottom-1.5 z-10 flex h-3 cursor-row-resize touch-none items-center justify-center"
              (pointerdown)="startRowResize($event, week)"
              (pointermove)="resizeRow($event, week)"
              (pointerup)="stopRowResize($event)"
              (pointercancel)="stopRowResize($event)"
              (dblclick)="toggleRowExpansion(week)"
            >
              <div
                class="h-1 w-10 rounded-full bg-gray-300 opacity-0 transition-opacity group-hover:opacity-100"
                [class.opacity-100]="resizingWeekKey() === week.key"
              ></div>
            </div>
          }
        </div>
      }

      @if (loadingBookings()) {
        <div
          class="absolute inset-0 flex items-center justify-center bg-white/60"
        >
          <app-loader></app-loader>
        </div>
      }
    </div>
  `,
  standalone: false,
})
export class BookingsMonthViewComponent implements OnInit {
  scope = input.required<BookingsScope>();

  private store = inject(Store);
  private modalService = inject(ModalService);
  private destroyRef = inject(DestroyRef);
  private focusedBooking = this.store.selectSignal(selectFocusedBooking);
  private translocoService = inject(TranslocoService);
  private bookingCalendarNavigationService = inject(
    BookingCalendarNavigationService,
  );

  private calendarRooms = inject(CalendarRoomsService);
  private calendarDate: Signal<Date>;
  private bookings: Signal<Booking[]>;
  loadingBookings: Signal<boolean>;

  rooms: Signal<Room[]> = computed(() =>
    this.calendarRooms.shownRooms(this.scope()),
  );

  weekDayLabels = computed(() => {
    const firstDayOfWeek = startOfWeek(new Date(), { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, index) =>
      addDays(firstDayOfWeek, index),
    );
  });

  monthWeeks: Signal<MonthWeek[]> = computed(() => {
    const month = this.calendarDate();
    const roomsById = new Map(this.rooms().map((room) => [room.id, room]));
    const displayedBookings = this.bookings()
      .filter((booking) => booking.block_type === BlockType.Booking)
      .filter((booking) => roomsById.has(booking.room_id))
      .sort((a, b) => a.start.localeCompare(b.start));
    const days = eachDayOfInterval({
      start: startOfWeek(startOfMonth(month), { weekStartsOn: 1 }),
      end: endOfWeek(endOfMonth(month), { weekStartsOn: 1 }),
    }).map((date) => ({
      date,
      isCurrentMonth: isSameMonth(date, month),
      isToday: isToday(date),
    }));

    const weeks: MonthWeek[] = [];
    for (let index = 0; index < days.length; index += DAYS_PER_WEEK) {
      const weekDays = days.slice(index, index + DAYS_PER_WEEK);
      const weekBookings = this.assignLanes(
        displayedBookings
          .filter((booking) =>
            this.overlapsWeek(
              booking,
              roomsById.get(booking.room_id),
              weekDays[0].date,
            ),
          )
          .map((booking) =>
            this.toMonthWeekBooking(
              booking,
              roomsById.get(booking.room_id),
              weekDays[0].date,
            ),
          ),
      );
      const laneCount = Math.max(
        0,
        ...weekBookings.map((weekBooking) => weekBooking.lane + 1),
      );
      weeks.push({
        key: format(weekDays[0].date, 'yyyy-MM-dd'),
        days: weekDays,
        bookings: weekBookings,
        laneCount,
        maxRowHeight: this.maxRowHeightFor(laneCount),
      });
    }
    return weeks;
  });

  private resizedRowHeights = signal<Record<string, number>>({});
  private activeRowResize: RowResize | null = null;
  resizingWeekKey = signal<string | null>(null);

  protected readonly BookingStatus = BookingStatus;
  protected readonly BOOKINGS_TOP_OFFSET_PX = BOOKINGS_TOP_OFFSET_PX;
  protected readonly BOOKING_HEIGHT_PX = BOOKING_HEIGHT_PX;
  protected readonly BOOKING_GAP_PX = BOOKING_GAP_PX;

  ngOnInit() {
    this.calendarDate = this.store.selectSignal(
      fromStore.selectBookingsCalendarDate(this.scope()),
    );
    this.bookings = this.store.selectSignal(selectAllBookings(this.scope()));
    this.loadingBookings = this.store.selectSignal(
      fromStore.selectBookingsLoading(this.scope()),
    );
  }

  rowHeightOf(week: MonthWeek): number {
    return Math.min(
      this.resizedRowHeights()[week.key] ?? DEFAULT_ROW_HEIGHT_PX,
      Math.max(week.maxRowHeight, DEFAULT_ROW_HEIGHT_PX),
    );
  }

  visibleLaneCountOf(week: MonthWeek): number {
    const rowHeight = this.rowHeightOf(week);
    if (this.heightToShowAll(week.laneCount) <= rowHeight) {
      return week.laneCount;
    }
    return Math.floor(
      (rowHeight - CELL_CHROME_HEIGHT_PX - MORE_LINK_HEIGHT_PX) /
        BOOKING_LINE_HEIGHT_PX,
    );
  }

  visibleBookingsOf(week: MonthWeek): MonthWeekBooking[] {
    const visibleLaneCount = this.visibleLaneCountOf(week);
    return week.bookings.filter(
      (weekBooking) => weekBooking.lane < visibleLaneCount,
    );
  }

  hiddenCountOn(week: MonthWeek, dayIndex: number): number {
    const visibleLaneCount = this.visibleLaneCountOf(week);
    return week.bookings.filter(
      (weekBooking) =>
        weekBooking.lane >= visibleLaneCount &&
        this.coversDayIndex(weekBooking, dayIndex),
    ).length;
  }

  gridColumnOf(weekBooking: MonthWeekBooking): string {
    const span = weekBooking.lastDayIndex - weekBooking.firstDayIndex + 1;
    return `${weekBooking.firstDayIndex + 1} / span ${span}`;
  }

  canExpand(week: MonthWeek): boolean {
    return week.maxRowHeight > DEFAULT_ROW_HEIGHT_PX;
  }

  startRowResize(event: PointerEvent, week: MonthWeek) {
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    this.activeRowResize = {
      weekKey: week.key,
      startY: event.clientY,
      startHeight: this.rowHeightOf(week),
    };
    this.resizingWeekKey.set(week.key);
  }

  resizeRow(event: PointerEvent, week: MonthWeek) {
    if (this.activeRowResize?.weekKey !== week.key) {
      return;
    }
    const draggedHeight =
      this.activeRowResize.startHeight +
      event.clientY -
      this.activeRowResize.startY;
    this.setRowHeight(
      week.key,
      Math.max(
        DEFAULT_ROW_HEIGHT_PX,
        Math.min(draggedHeight, week.maxRowHeight),
      ),
    );
  }

  stopRowResize(event: PointerEvent) {
    const handle = event.currentTarget as HTMLElement;
    if (handle.hasPointerCapture(event.pointerId)) {
      handle.releasePointerCapture(event.pointerId);
    }
    this.activeRowResize = null;
    this.resizingWeekKey.set(null);
  }

  toggleRowExpansion(week: MonthWeek) {
    const isExpanded = this.rowHeightOf(week) >= week.maxRowHeight;
    this.setRowHeight(
      week.key,
      isExpanded ? DEFAULT_ROW_HEIGHT_PX : week.maxRowHeight,
    );
  }

  isPending(booking: Booking): boolean {
    return (
      booking.status === BookingStatus.Pending ||
      booking.status === BookingStatus.PendingPayment
    );
  }

  openBooking(booking: Booking) {
    const instance = this.modalService.open(BookingDetailsModalComponent, {});
    (instance.componentRef.instance as BookingDetailsModalComponent).bookingId =
      booking.id;
  }

  canCreateBooking = computed(() =>
    this.rooms().some((room) => this.isRoomBookablePrivately(room)),
  );

  openCreateBookingModal(date: Date) {
    if (!this.canCreateBooking()) {
      return;
    }
    const startDate = setHours(
      startOfDay(date),
      DEFAULT_NEW_BOOKING_START_HOUR,
    );
    const instance = this.modalService.open(PrivateBookingMainFormComponent, {
      title: this.translocoService.translate(
        'bookings.calendar-room.new-booking-modal-title',
      ),
      okAction: {
        title: this.translocoService.translate('common.button.save'),
      },
    });
    const component = instance.componentRef
      .instance as PrivateBookingMainFormComponent;
    component.initialData = {
      roomId: this.defaultRoomIdForNewBooking(),
      startDate,
      endDate: addHours(startDate, 1),
    };
    outputToObservable(component.bookingSaved)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((bookingStartDate) => {
        this.rememberLastUsedRoomId(this.focusedBooking()?.room_id);
        this.bookingCalendarNavigationService.handlePrivateBookingSaved(
          bookingStartDate,
        );
      });
  }

  openDay(date: Date) {
    this.store.dispatch(
      bookingsActions.setCalendarDate({ date, scope: this.scope() }),
    );
    this.store.dispatch(
      bookingsActions.setDisplayMode({
        displayMode: BookingsDisplayMode.Day,
        scope: this.scope(),
      }),
    );
  }

  private defaultRoomIdForNewBooking(): number | undefined {
    const bookableRooms = this.rooms().filter((room) =>
      this.isRoomBookablePrivately(room),
    );
    const lastUsedRoomId = this.readLastUsedRoomId();
    const lastUsedRoom = bookableRooms.find(
      (room) => room.id === lastUsedRoomId,
    );
    return (lastUsedRoom ?? bookableRooms[0])?.id;
  }

  private readLastUsedRoomId(): number | null {
    try {
      return Number(localStorage.getItem(LAST_USED_ROOM_STORAGE_KEY)) || null;
    } catch {
      return null;
    }
  }

  private rememberLastUsedRoomId(roomId: number | null) {
    if (!roomId) {
      return;
    }
    try {
      localStorage.setItem(LAST_USED_ROOM_STORAGE_KEY, String(roomId));
    } catch {
      return;
    }
  }

  private isRoomBookablePrivately(room: Room): boolean {
    return (
      room.bookable_by === BookableBy.Private ||
      room.bookable_by === BookableBy.Both
    );
  }

  private setRowHeight(weekKey: string, height: number) {
    this.resizedRowHeights.update((heights) => ({
      ...heights,
      [weekKey]: height,
    }));
  }

  private heightToShowAll(bookingsCount: number): number {
    return CELL_CHROME_HEIGHT_PX + bookingsCount * BOOKING_LINE_HEIGHT_PX;
  }

  private maxRowHeightFor(bookingsCount: number): number {
    return bookingsCount > MAX_VISIBLE_BOOKINGS_PER_DAY
      ? this.heightToShowAll(MAX_VISIBLE_BOOKINGS_PER_DAY) + MORE_LINK_HEIGHT_PX
      : this.heightToShowAll(bookingsCount);
  }

  private overlapsWeek(booking: Booking, room: Room, weekStart: Date): boolean {
    return (
      !isBefore(this.displayedEndOf(booking, room), weekStart) &&
      isBefore(parseISO(booking.start), addDays(weekStart, DAYS_PER_WEEK))
    );
  }

  private displayedEndOf(booking: Booking, room: Room): Date {
    const start = parseISO(booking.start);
    const end = parseISO(booking.end);
    if (differenceInHours(end, start) < HOURS_PER_DAY) {
      return start;
    }
    return BookingLastOccupiedDayHelper.lastOccupiedDayOf(booking, room);
  }

  private toMonthWeekBooking(
    booking: Booking,
    room: Room,
    weekStart: Date,
  ): MonthWeekBooking {
    const start = parseISO(booking.start);
    const startDayIndex = differenceInCalendarDays(start, weekStart);
    const endDayIndex = differenceInCalendarDays(
      this.displayedEndOf(booking, room),
      weekStart,
    );
    const firstDayIndex = Math.max(0, startDayIndex);
    return {
      booking,
      color: this.calendarRooms.colorOf(room),
      startTime: format(start, 'HH:mm'),
      isAllDay: this.coversWholeDays(booking),
      tooltip: BookingTooltipHelper.tooltipOf(booking, room, (key) =>
        this.translocoService.translate(key),
      ),
      firstDayIndex,
      lastDayIndex: Math.min(DAYS_PER_WEEK - 1, endDayIndex),
      continuesFromPreviousWeek: startDayIndex < 0,
      continuesToNextWeek: endDayIndex >= DAYS_PER_WEEK,
      lane: 0,
    };
  }

  private assignLanes(weekBookings: MonthWeekBooking[]): MonthWeekBooking[] {
    const occupiedDaysByLane: boolean[][] = [];
    return [...weekBookings]
      .sort(
        (a, b) =>
          a.firstDayIndex - b.firstDayIndex ||
          b.lastDayIndex - a.lastDayIndex ||
          a.booking.start.localeCompare(b.booking.start),
      )
      .map((weekBooking) => {
        let lane = occupiedDaysByLane.findIndex((occupiedDays) =>
          this.isLaneFree(occupiedDays, weekBooking),
        );
        if (lane === -1) {
          lane = occupiedDaysByLane.push([]) - 1;
        }
        for (
          let dayIndex = weekBooking.firstDayIndex;
          dayIndex <= weekBooking.lastDayIndex;
          dayIndex++
        ) {
          occupiedDaysByLane[lane][dayIndex] = true;
        }
        return { ...weekBooking, lane };
      });
  }

  private isLaneFree(
    occupiedDays: boolean[],
    weekBooking: MonthWeekBooking,
  ): boolean {
    for (
      let dayIndex = weekBooking.firstDayIndex;
      dayIndex <= weekBooking.lastDayIndex;
      dayIndex++
    ) {
      if (occupiedDays[dayIndex]) {
        return false;
      }
    }
    return true;
  }

  private coversDayIndex(
    weekBooking: MonthWeekBooking,
    dayIndex: number,
  ): boolean {
    return (
      weekBooking.firstDayIndex <= dayIndex &&
      dayIndex <= weekBooking.lastDayIndex
    );
  }

  private coversWholeDays(booking: Booking): boolean {
    const start = parseISO(booking.start);
    const end = parseISO(booking.end);
    return (
      booking.all_day ||
      (start.getTime() === startOfDay(start).getTime() &&
        end.getTime() === startOfDay(end).getTime())
    );
  }
}
